import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Building2, FolderKanban, Boxes, FileText, Truck, Inbox, Search, Plus, Sparkles, Receipt, FileSignature } from "lucide-react";
import { useAssistantOptional } from "@/context/AssistantContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const ICON = { customer: Building2, project: FolderKanban, module: Boxes, quote: FileText, supplier: Truck, lead: Inbox, invoice: Receipt, contract: FileSignature };
const GROUP = { customer: "Kunder", project: "Prosjekter", module: "Moduler", quote: "Tilbud", supplier: "Leverandører", lead: "Henvendelser", invoice: "Fakturaer", contract: "Kontrakter" };

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { can } = useAuth();
  const assistant = useAssistantOptional();
  const timer = useRef();

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await get("/search", { q });
        setResults(r.results || []);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [q]);

  const go = (url) => {
    setOpen(false);
    setQ("");
    navigate(url);
  };

  const groups = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  const actions = [
    can("leads:write") && { label: "Ny henvendelse", url: "/henvendelser?ny=1" },
    can("customers:write") && { label: "Ny kunde", url: "/kunder?ny=1" },
    can("projects:write") && { label: "Nytt prosjekt", url: "/prosjekter?ny=1" },
    can("modules:write") && { label: "Ny modul", url: "/moduler?ny=1" },
    can("issues:write") && { label: "Registrer avvik", url: "/service?tab=avvik&ny=1" },
    can("finance:write") && { label: "Ny faktura", url: "/okonomi/fakturaer?ny=1" },
    can("finance:write") && { label: "Registrer leverandørkostnad", url: "/okonomi/kostnader?ny=1" },
    can("finance:read") && { label: "Økonomi-oversikt", url: "/okonomi" },
    can("planning:read") && { label: "Kapasitetssjekk – kan vi levere?", url: "/planlegging/kapasitet" },
    can("innkvartering:read") && { label: "Innkvartering – belegg og beredskap", url: "/innkvartering" },
  ].filter(Boolean);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-open-button"
        aria-label="Søk"
        className="inline-flex h-8 items-center gap-2 rounded-md px-2 text-[12.5px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:bg-muted sm:w-52 sm:border sm:border-input sm:bg-background sm:pl-2.5 sm:pr-1.5"
      >
        <Search className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        <span className="hidden min-w-0 flex-1 truncate text-left sm:inline">Søk…</span>
        <kbd className="kbd hidden sm:inline-flex">⌘K</kbd>
      </button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Søk på kunde, org.nr, prosjekt, adresse, SI-184, TIL-2026…" value={q} onValueChange={setQ} data-testid="global-search-input" />
          <CommandList>
            {q.length >= 2 && !loading && results.length === 0 && <CommandEmpty>Ingen treff på «{q}»</CommandEmpty>}
            {q.trim().length >= 3 && assistant && (
              <CommandGroup heading="Assistent">
                <CommandItem
                  value={`ask-${q}`}
                  onSelect={() => {
                    setOpen(false);
                    assistant.ask(q.trim());
                  }}
                  className="flex items-center gap-2"
                  data-testid="global-search-ask-ai"
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="min-w-0 flex-1 truncate text-sm">Spør assistenten: «{q.trim()}»</span>
                  <kbd className="kbd">⌘J</kbd>
                </CommandItem>
              </CommandGroup>
            )}
            {loading && <div className="py-6 text-center text-sm text-muted-foreground">Søker…</div>}
            {Object.entries(groups).map(([type, items]) => {
              const Icon = ICON[type] || Search;
              return (
                <CommandGroup key={type} heading={GROUP[type] || type}>
                  {items.map((r) => (
                    <CommandItem key={`${type}-${r.id}`} value={`${type}-${r.id}`} onSelect={() => go(r.url)} data-testid="global-search-result-item" className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{r.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>
                      </div>
                      {r.status && <StatusBadge status={r.status} />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
            {q.length < 2 && actions.length > 0 && (
              <CommandGroup heading="Hurtighandlinger">
                {actions.map((a) => (
                  <CommandItem key={a.url} value={a.url} onSelect={() => go(a.url)} className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-muted-foreground" /> {a.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
};
